import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useHaptics } from "./use_haptics";
import { useTabNavigation } from "./use_tab_navigation";
import { TAB_MOTION_MS } from "@/lib/feel";
import { prefersReducedMotion } from "@/lib/motion";
import { neighbourTab, tabAxis, tabCommit, tabEligible, tabOffset, tabThreshold } from "@/lib/tab_swipe";
const CLICK_GUARD_MS = 500;
/** The innermost owner keeps the entire gesture, even when it reaches its end. */
export function tabSwipeClaimed(target: Element): boolean {
  if (target.closest('[data-swipe-claim], input, textarea, select, [contenteditable], [role="slider"]')) return true;
  for (let node: Element | null = target; node; node = node.parentElement) {
    if (node.scrollWidth > node.clientWidth && /^(auto|scroll)$/.test(getComputedStyle(node).overflowX)) return true;
  }
  return false;
}
export function useTabSwipe(): void {
  const location = useLocation();
  const pathname = location.pathname === "/family" ? "/home" : location.pathname;
  const navigateTab = useTabNavigation();
  const haptic = useHaptics();
  const swallowUntil = useRef(0);
  useEffect(() => {
    let drag: { id: number; x: number; y: number; time: number; dx: number; axis: "x" | "y" | null; armed: boolean; width: number; host: HTMLElement } | null = null;
    const springs = new Map<HTMLElement, ReturnType<typeof setTimeout>>();
    const blocked = (): boolean => !!document.querySelector('[role="dialog"], [data-ledger-selecting="true"]');
    const reset = (spring: boolean): void => {
      if (drag) {
        const host = drag.host;
        clearTimeout(springs.get(host)); springs.delete(host);
        host.style.transition = spring && !prefersReducedMotion() ? "transform var(--feel-tab) var(--feel-spring)" : "";
        host.style.transform = "";
        if (host.style.transition) springs.set(host, setTimeout(() => { host.style.transition = ""; springs.delete(host); }, TAB_MOTION_MS));
      }
      drag = null;
    };
    const start = (event: TouchEvent): void => {
      reset(false);
      swallowUntil.current = 0;
      const point = event.touches[0];
      const target = event.target;
      if (!point || event.touches.length !== 1 || !(target instanceof Element)) return;
      const host = target.closest<HTMLElement>("[data-shell-routes]");
      if (!tabEligible({ path: pathname, pointerType: "touch", width: window.innerWidth, x: point.clientX, dialog: !!document.querySelector('[role="dialog"]'), selecting: !!document.querySelector('[data-ledger-selecting="true"]'), routeRoot: host !== null }) || !host || tabSwipeClaimed(target)) return;
      clearTimeout(springs.get(host)); springs.delete(host); host.style.transition = "";
      drag = { id: point.identifier, x: point.clientX, y: point.clientY, time: event.timeStamp, dx: 0, axis: null, armed: false, width: window.innerWidth, host };
    };
    const move = (event: TouchEvent): void => {
      if (!drag) return;
      const point = Array.from(event.touches).find(point => point.identifier === drag?.id);
      if (!point || event.touches.length !== 1 || blocked()) { reset(true); return; }
      drag.dx = point.clientX - drag.x;
      drag.axis ??= tabAxis(drag.dx, point.clientY - drag.y);
      if (drag.axis !== "x") return;
      const to = neighbourTab(pathname, drag.dx < 0 ? "forward" : "back");
      const armed = to !== null && Math.abs(drag.dx) >= tabThreshold(drag.width);
      if (armed && !drag.armed) haptic("threshold");
      drag.armed = armed;
      if (!prefersReducedMotion()) { drag.host.style.transition = "none"; drag.host.style.transform = `translateX(${String(tabOffset(drag.dx, to !== null))}px)`; }
    };
    const end = (event: TouchEvent): void => {
      if (event.type === "touchcancel") { reset(true); return; }
      if (!drag) return;
      const point = Array.from(event.changedTouches).find(point => point.identifier === drag?.id);
      if (!point) return;
      const state = drag;
      const dx = point.clientX - state.x;
      const direction = dx < 0 ? "forward" : "back";
      const to = neighbourTab(pathname, direction);
      const commit = !blocked() && state.axis === "x" && to !== null && tabCommit(dx, state.width, event.timeStamp - state.time);
      if (commit && to && navigateTab(to, direction, () => reset(false))) {
        swallowUntil.current = performance.now() + CLICK_GUARD_MS; haptic("select");
      } else reset(true);
    };
    const click = (event: MouseEvent): void => { if (performance.now() < swallowUntil.current) { swallowUntil.current = 0; event.preventDefault(); event.stopImmediatePropagation(); } };
    const visibility = (): void => { if (document.visibilityState === "hidden") reset(false); };
    document.addEventListener("visibilitychange", visibility);
    document.addEventListener("touchstart", start, { passive: true });
    document.addEventListener("touchmove", move, { passive: true });
    document.addEventListener("touchend", end, { passive: true });
    document.addEventListener("touchcancel", end, { passive: true });
    document.addEventListener("click", click, true);
    return () => { reset(false); for (const [host, timer] of springs) { clearTimeout(timer); host.style.transition = ""; } document.removeEventListener("visibilitychange", visibility); document.removeEventListener("touchstart", start); document.removeEventListener("touchmove", move); document.removeEventListener("touchend", end); document.removeEventListener("touchcancel", end); document.removeEventListener("click", click, true); };
  }, [pathname, navigateTab, haptic]);
}
